import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authAPI } from '../services/api';
import { staffAPI, confidentialAPI } from '../services/api';
import toast from '../utils/toast';

const AuthContext = createContext();

// Auth reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken,
        loading: false,
        error: null,
      };
    case 'TOKEN_REFRESHED':
      // A token refresh only mints new tokens for the SAME user — it must NOT
      // wipe server-computed profile flags. The JWT carries scopes/is_superuser
      // but NOT flags like is_asset_custodian / is_reporting_officer / is_hod
      // (those come from getCurrentUser via hydrateUserFromProfile). Replacing
      // the user wholesale with the bare JWT decode made the Asset Admin Portal
      // button vanish on every refresh until a manual page reload. Merge the
      // fresh JWT claims OVER the existing profile so those flags survive.
      return {
        ...state,
        user: { ...state.user, ...action.payload.user },
        token: action.payload.token,
        refreshToken: action.payload.refreshToken,
        loading: false,
        error: null,
      };
    case 'LOGIN_ERROR':
      return {
        ...state,
        user: null,
        token: null,
        refreshToken: null,
        consentObtained: null,
        consentLoading: false,
        requiresPasswordChange: null,
        loading: false,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        refreshToken: null,
        consentObtained: null,
        consentLoading: false,
        requiresPasswordChange: null,
        loading: false,
        error: null,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };
    case 'SET_CONSENT_STATUS':
      return {
        ...state,
        consentObtained: action.payload,
        consentLoading: false,
      };
    case 'SET_CONSENT_LOADING':
      return {
        ...state,
        consentLoading: action.payload,
      };
    case 'SET_REQUIRES_PASSWORD_CHANGE':
      return {
        ...state,
        requiresPasswordChange: action.payload,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

// Non-remembered sessions use an expiry sentinel in localStorage instead of sessionStorage.
// This survives iOS WKWebView process termination while still expiring when the window closes
// or after the refresh token lifetime (12 h default, 30 days when Remember Me is checked).
const SESSION_DURATION_MS = 12 * 60 * 60 * 1000; // matches default server refresh token lifetime

// On module load: clear storage when a non-remembered session has passed its expiry.
// This runs synchronously so initialState below already reflects the cleared state.
(() => {
  const expiry = localStorage.getItem('sessionExpiry');
  if (expiry && Date.now() > parseInt(expiry, 10)) {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('sessionExpiry');
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
  }
})();

const initialState = {
  user: null,
  token: localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken'),
  refreshToken: localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken'),
  consentObtained: null, // null = unknown, true = consented, false = needs consent
  consentLoading: false, // true while fetching consent status
  requiresPasswordChange: null, // null = unknown, true = must change, false = password OK
  loading: true,
  error: null,
};

const decodeJWT = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const queryClient = useQueryClient();
  const refreshTimerRef = useRef(null);
  const isRefreshingRef = useRef(false);

  const purgeSwCaches = () => {
    try {
      if (navigator?.serviceWorker?.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'PURGE_AUTH_CACHES' });
      }
    } catch (e) {
      // Non-fatal; proceed even if SW purge is unavailable
      console.debug('SW purge not available', e);
    }
  };

  // Helper to merge server-side profile flags (e.g., is_reporting_officer, requires_password_change)
  const hydrateUserFromProfile = async () => {
    try {
      const res = await staffAPI.getCurrentUser();
      const profile = res?.data || {};
      dispatch({ type: 'UPDATE_USER', payload: profile });
      
      // System accounts (employment_classification='system') skip onboarding
      // They don't need PDPA consent or forced password change
      if (profile.skip_onboarding) {
        dispatch({ type: 'SET_CONSENT_STATUS', payload: true });
        dispatch({ type: 'SET_REQUIRES_PASSWORD_CHANGE', payload: false });
        return { requiresPasswordChange: false, skipOnboarding: true };
      }
      
      // Set password change requirement status from profile
      const requiresChange = profile.requires_password_change === true;
      dispatch({ type: 'SET_REQUIRES_PASSWORD_CHANGE', payload: requiresChange });
      return { requiresPasswordChange: requiresChange, skipOnboarding: false };
    } catch (err) {
      // Non-blocking; keep decoded JWT info if profile fetch fails
      console.debug('Auth hydrateUserFromProfile failed', err?.message || err);
      // Set is_reporting_officer to a known false so RO-dependent queries
      // are correctly disabled rather than permanently stuck as undefined.
      dispatch({ type: 'UPDATE_USER', payload: { is_reporting_officer: false } });
      dispatch({ type: 'SET_REQUIRES_PASSWORD_CHANGE', payload: false });
      return { requiresPasswordChange: false, skipOnboarding: false };
    }
  };

  // Re-check password change status (call on sensitive navigation)
  // This handles the race condition where admin resets password while user is logged in
  const recheckPasswordStatus = async () => {
    if (!state.user || !state.token) return;
    try {
      const res = await staffAPI.getCurrentUser();
      const profile = res?.data || {};
      const requiresChange = profile.requires_password_change === true;
      
      // Only update if status changed to requiring password change
      if (requiresChange && !state.requiresPasswordChange) {
        dispatch({ type: 'SET_REQUIRES_PASSWORD_CHANGE', payload: true });
        toast.error('Your password has been reset. Please set a new password.');
      }
    } catch (err) {
      console.debug('recheckPasswordStatus failed', err?.message || err);
    }
  };

  // Fetch consent status from HR profile
  const fetchConsentStatus = async () => {
    dispatch({ type: 'SET_CONSENT_LOADING', payload: true });
    try {
      const res = await confidentialAPI.getMyHRProfile();
      const hrProfile = res?.data || {};
      const consentStatus = hrProfile.hrprofile_consent_obtained === true;
      dispatch({ type: 'SET_CONSENT_STATUS', payload: consentStatus });
      return consentStatus;
    } catch (err) {
      // If HR profile doesn't exist, assume consent not obtained
      console.debug('fetchConsentStatus failed', err?.message || err);
      dispatch({ type: 'SET_CONSENT_STATUS', payload: false });
      return false;
    }
  };

  // Accept consent - updates HR profile
  const acceptConsent = async () => {
    try {
      const res = await confidentialAPI.acceptConsent();
      if (res?.data?.success || res?.status === 200) {
        dispatch({ type: 'SET_CONSENT_STATUS', payload: true });
        toast.success('Privacy policy acknowledged successfully');
        return true;
      }
      return false;
    } catch (err) {
      console.error('acceptConsent failed', err);
      toast.error('Failed to record consent. Please try again.');
      return false;
    }
  };

  // Change password - for first-time login password change
  const changePassword = async (currentPassword, newPassword) => {
    try {
      const res = await authAPI.changePassword(currentPassword, newPassword);
      if (res?.data?.success || res?.status === 200) {
        dispatch({ type: 'SET_REQUIRES_PASSWORD_CHANGE', payload: false });
        toast.success('Password changed successfully!');
        return { success: true };
      }
      return { success: false, error: 'Failed to change password.' };
    } catch (err) {
      console.error('changePassword failed', err);
      const errorMessage = err.response?.data?.error || 'Failed to change password. Please try again.';
      return { success: false, error: errorMessage };
    }
  };

  // Silently refresh the access token using the refresh token in localStorage.
  // Unlike refreshAccessToken, this does NOT logout on failure — returns null instead.
  // Used for proactive refresh and init-time recovery where a failed refresh should
  // not nuke the session (the 401 interceptor is the last line of defense for that).
  const silentRefresh = async () => {
    if (isRefreshingRef.current) return null;
    isRefreshingRef.current = true;
    try {
      const currentRefresh = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
      if (!currentRefresh) return null;

      const response = await authAPI.refreshToken(currentRefresh);
      const { access, refresh: newRefreshToken } = response.data;

      localStorage.setItem('accessToken', access);
      if (newRefreshToken) {
        localStorage.setItem('refreshToken', newRefreshToken);
        if (localStorage.getItem('sessionExpiry')) {
          localStorage.setItem('sessionExpiry', String(Date.now() + SESSION_DURATION_MS));
        }
      }

      const userInfo = decodeJWT(access);
      dispatch({
        type: 'TOKEN_REFRESHED',
        payload: {
          user: userInfo,
          token: access,
          refreshToken: newRefreshToken || currentRefresh,
        },
      });

      return access;
    } catch {
      return null;
    } finally {
      isRefreshingRef.current = false;
    }
  };

  // Schedule a proactive token refresh ~60s before the access token expires.
  // This keeps the session alive transparently as long as the refresh token is valid,
  // so users never hit a 401 just because they left a tab open.
  const scheduleProactiveRefresh = (accessToken) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const payload = decodeJWT(accessToken);
    if (!payload?.exp) return;

    const msUntilExpiry = payload.exp * 1000 - Date.now();
    const refreshIn = Math.max(msUntilExpiry - 60_000, 10_000);

    refreshTimerRef.current = setTimeout(async () => {
      const newAccess = await silentRefresh();
      if (newAccess) {
        scheduleProactiveRefresh(newAccess);
        // silentRefresh now MERGES JWT claims over the existing user (TOKEN_REFRESHED),
        // so server-computed flags (is_asset_custodian, is_reporting_officer, is_hod)
        // already survive the refresh. We still re-hydrate here so any server-side role
        // changes made mid-session (e.g. an admin granting custodian access) are picked
        // up without requiring the user to reload.
        hydrateUserFromProfile();
      }
    }, refreshIn);
  };

  // Check if user is authenticated on app load
  useEffect(() => {
    const initAuth = async () => {
      // Migrate legacy sessionStorage sessions to localStorage for iOS/Android PWA compatibility.
      const sessionAccessToken = sessionStorage.getItem('accessToken');
      const sessionRefreshToken = sessionStorage.getItem('refreshToken');
      if (sessionAccessToken && !localStorage.getItem('accessToken')) {
        localStorage.setItem('accessToken', sessionAccessToken);
        if (sessionRefreshToken) localStorage.setItem('refreshToken', sessionRefreshToken);
        localStorage.setItem('sessionExpiry', String(Date.now() + SESSION_DURATION_MS));
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('refreshToken');
      }

      const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');

      if (token && refreshToken) {
        try {
          const userInfo = decodeJWT(token);
          if (userInfo && userInfo.exp * 1000 > Date.now()) {
            dispatch({
              type: 'LOGIN_SUCCESS',
              payload: { user: userInfo, token, refreshToken },
            });
            scheduleProactiveRefresh(token);
            const hydrateResult = await hydrateUserFromProfile();
            if (!hydrateResult?.skipOnboarding) {
              fetchConsentStatus();
            }
          } else {
            // Access token expired — try refreshing before giving up.
            // The refresh token may still be valid (up to 12h / 30d with remember-me).
            const newAccess = await silentRefresh();
            if (newAccess) {
              scheduleProactiveRefresh(newAccess);
              const hydrateResult = await hydrateUserFromProfile();
              if (!hydrateResult?.skipOnboarding) {
                fetchConsentStatus();
              }
            } else {
              logout();
            }
          }
        } catch (error) {
          logout();
        }
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initAuth();

    // When the user returns to the tab after being away, check if the access token
    // needs refreshing and do it proactively so they never see a login redirect.
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;

      const token = localStorage.getItem('accessToken');
      const refresh = localStorage.getItem('refreshToken');
      if (!token || !refresh) return;

      const payload = decodeJWT(token);
      if (!payload?.exp) return;

      const msUntilExpiry = payload.exp * 1000 - Date.now();
      if (msUntilExpiry < 2 * 60 * 1000) {
        const newAccess = await silentRefresh();
        if (newAccess) {
          scheduleProactiveRefresh(newAccess);
        }
      } else {
        scheduleProactiveRefresh(token);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  // Login function
  const login = async (identifier, password, rememberMe = false) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      const response = await authAPI.login(identifier, password, rememberMe);
      const { access, refresh } = response.data;

      // Always use localStorage for iOS/Android PWA compatibility (sessionStorage is cleared
      // when iOS terminates the WKWebView process in the background).
      // Non-remembered sessions are bounded by a short-lived expiry sentinel instead.
      localStorage.setItem('accessToken', access);
      localStorage.setItem('refreshToken', refresh);
      if (rememberMe) {
        localStorage.removeItem('sessionExpiry'); // No sentinel = persistent remembered session
      } else {
        localStorage.setItem('sessionExpiry', String(Date.now() + SESSION_DURATION_MS));
      }
      // Clean up any legacy sessionStorage tokens
      sessionStorage.removeItem('accessToken');
      sessionStorage.removeItem('refreshToken');
      
      // Decode user info from token
      const userInfo = decodeJWT(access);
      
      // Clear any cached queries before setting new user context
      try { queryClient.clear(); } catch {}
      purgeSwCaches();

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: userInfo,
          token: access,
          refreshToken: refresh,
        },
      });

      scheduleProactiveRefresh(access);

      // Enrich with server-side profile flags
      // hydrateUserFromProfile will set consent status for system accounts
      const hydrateResult = await hydrateUserFromProfile();
      // Only fetch consent status for non-system accounts
      if (!hydrateResult?.skipOnboarding) {
        fetchConsentStatus();
      }
      
      toast.success('Welcome back!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      dispatch({
        type: 'LOGIN_ERROR',
        payload: message,
      });
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Logout function
  const logout = () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('sessionExpiry');
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
    
    // Clear client caches and purge SW caches to avoid cross-user bleed
    try { queryClient.clear(); } catch {}
    purgeSwCaches();

    dispatch({ type: 'LOGOUT' });
    toast.success('Logged out successfully');
  };

  // Refresh access token
  const refreshAccessToken = async (refreshToken) => {
    try {
      const response = await authAPI.refreshToken(refreshToken);
      const { access, refresh: newRefreshToken } = response.data;

      // Migrate any legacy sessionStorage session to localStorage on first refresh
      const wasInSessionStorage = !localStorage.getItem('refreshToken') && !!sessionStorage.getItem('refreshToken');
      if (wasInSessionStorage) {
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('refreshToken');
        localStorage.setItem('sessionExpiry', String(Date.now() + SESSION_DURATION_MS));
      }

      localStorage.setItem('accessToken', access);

      // Save the rotated refresh token — critical when ROTATE_REFRESH_TOKENS=True on the backend.
      // Without this, the next refresh attempt sends a blacklisted token and forces a logout.
      if (newRefreshToken) {
        localStorage.setItem('refreshToken', newRefreshToken);
        // Slide the expiry window forward so active non-remembered sessions stay alive
        if (localStorage.getItem('sessionExpiry')) {
          localStorage.setItem('sessionExpiry', String(Date.now() + SESSION_DURATION_MS));
        }
      }

      // Update state with the new tokens
      const userInfo = decodeJWT(access);
      dispatch({
        type: 'TOKEN_REFRESHED',
        payload: {
          user: userInfo,
          token: access,
          refreshToken: newRefreshToken || refreshToken,
        },
      });

      scheduleProactiveRefresh(access);
      return access;
    } catch (error) {
      logout();
      throw error;
    }
  };

  // Update user profile
  const updateUser = (userData) => {
    dispatch({
      type: 'UPDATE_USER',
      payload: userData,
    });
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Check if user is admin
  const isAdmin = () => {
    return state.user?.scopes?.includes('confidential') || state.user?.is_superuser || false;
  };

  // Check if user has specific scope
  const hasScope = (scope) => {
    return state.user?.scopes?.includes(scope) || false;
  };

  // Check if user can access admin features
  const canAccessAdmin = () => {
    return isAdmin() || state.user?.is_superuser || false;
  };

  const value = {
    ...state,
    login,
    logout,
    refreshAccessToken,
    updateUser,
    clearError,
    isAdmin,
    hasScope,
    canAccessAdmin,
    fetchConsentStatus,
    acceptConsent,
    changePassword,
    recheckPasswordStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 